import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  RadarController,
  RadialLinearScale,
  Title,
  Tooltip,
} from 'chart.js';
import React, { useEffect, useMemo, useState } from 'react';
import { Bar, Doughnut, Line, Pie } from 'react-chartjs-2';
import { useScreenLogging } from '../../../hooks/useScreenLogging';
import { typography } from '../../../styles/typography';
import { getAllSites } from '../../SiteManagement/siteApi';
import Card from '../../ui/card';
import { salesDashboardApi } from './SaleAnalyticsApi';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  RadialLinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  RadarController,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Format date for display
const formatDate = (date) => {
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

// Format date for API (YYYY-MM-DD)
const formatDateForAPI = (date) => {
  return date.toISOString().split('T')[0];
};

// Get current date
const getCurrentDate = () => {
  return new Date();
};

// Generate dummy sales data based on date range and site
const generateDummySalesData = (startDate, endDate, selectedSiteId) => {
  const customers = [
    'TechCorp Industries',
    'Global Solutions Ltd.',
    'Prime Business Group',
    'Elite Enterprises',
    'Premium Services Co.',
    'Advanced Systems Inc.',
    'Mega Corporation',
    'Innovation Hub',
    'Strategic Partners',
    'Dynamic Solutions',
    'Apex Business Solutions',
    'Prime Ventures',
    'Elite Commerce',
    'Premium Retailers',
    'Advanced Trading Co.',
  ];

  const products = [
    { name: 'Paneer Sandwich', category: 'Food', basePrice: 120 },
    { name: 'Veg Burger', category: 'Food', basePrice: 95 },
    { name: 'Chicken Burger', category: 'Food', basePrice: 150 },
    { name: 'French Fries', category: 'Food', basePrice: 80 },
    { name: 'Pizza Margherita', category: 'Food', basePrice: 250 },
    { name: 'Pizza Pepperoni', category: 'Food', basePrice: 280 },
    { name: 'Coffee', category: 'Beverages', basePrice: 50 },
    { name: 'Cappuccino', category: 'Beverages', basePrice: 75 },
    { name: 'Latte', category: 'Beverages', basePrice: 85 },
    { name: 'Cold Coffee', category: 'Beverages', basePrice: 90 },
    { name: 'Mojito', category: 'Beverages', basePrice: 120 },
    { name: 'Fresh Juice', category: 'Beverages', basePrice: 100 },
    { name: 'Chocolate Cake', category: 'Desserts', basePrice: 150 },
    { name: 'Ice Cream', category: 'Desserts', basePrice: 80 },
    { name: 'Brownie', category: 'Desserts', basePrice: 120 },
  ];

  const sites = [
    { id: 'site-001', name: 'Koramangala Outlet' },
    { id: 'site-002', name: 'Indiranagar Branch' },
    { id: 'site-003', name: 'Whitefield Center' },
    { id: 'site-004', name: 'MG Road Store' },
    { id: 'site-005', name: 'Electronic City' },
  ];

  const paymentMethods = ['Cash', 'Card', 'UPI', 'Wallet', 'Online'];

  // Generate customer data
  const customerData = customers.map((customer, index) => {
    const orderCount = Math.floor(Math.random() * 100) + 20;
    const totalSpending = Math.floor(Math.random() * 2000000) + 100000;
    const averageOrderValue = totalSpending / orderCount;

    return {
      customer,
      orderCount,
      totalSpending,
      averageOrderValue,
    };
  });

  // Generate daily revenue and profit data
  const days = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
  const dailyRevenue = [];
  const dailyProfit = [];
  const dailyProfitMargin = [];
  const revenueProfitComparison = [];
  const poInvoiceEstimationData = [];

  for (let i = 0; i < days; i++) {
    const currentDate = new Date(startDate);
    currentDate.setDate(startDate.getDate() + i);
    
    // Generate revenue with some variation
    const baseRevenue = 50000 + Math.random() * 100000;
    const revenue = Math.floor(baseRevenue + Math.sin(i / 7) * 20000);
    
    // Profit margin between 15% and 35%
    const profitMargin = 15 + Math.random() * 20;
    const profit = Math.floor(revenue * (profitMargin / 100));
    const costOfGoods = revenue - profit;
    
    dailyRevenue.push({
      date: formatDateForAPI(currentDate),
      revenue,
      orders: Math.floor(Math.random() * 200) + 100,
      customers: Math.floor(Math.random() * 100) + 50,
    });
    
    dailyProfit.push({
      date: formatDateForAPI(currentDate),
      profit,
    });
    
    dailyProfitMargin.push({
      date: formatDateForAPI(currentDate),
      margin: profitMargin,
      revenue,
      profit,
      costOfGoods,
    });

    revenueProfitComparison.push({
      date: formatDateForAPI(currentDate),
      revenue,
      profit,
    });

    // Generate PO, Invoice, and Estimation/Quotation data
    const basePO = 20 + Math.floor(Math.random() * 30);
    const baseInvoice = 15 + Math.floor(Math.random() * 25);
    const baseEstimation = 10 + Math.floor(Math.random() * 20);
    
    poInvoiceEstimationData.push({
      date: formatDateForAPI(currentDate),
      purchase_orders: basePO + Math.floor(Math.sin(i / 5) * 5),
      invoices: baseInvoice + Math.floor(Math.cos(i / 5) * 5),
      estimations: baseEstimation + Math.floor(Math.sin(i / 6) * 4),
    });
  }

  // Generate top products
  const topProducts = products.map((product) => {
    const quantitySold = Math.floor(Math.random() * 500) + 50;
    const avgSellingPrice = product.basePrice + Math.floor(Math.random() * 30) - 15;
    const revenue = quantitySold * avgSellingPrice;
    const costPerUnit = product.basePrice * 0.65; // 35% margin
    const profit = revenue - (quantitySold * costPerUnit);
    const marginPercentage = (profit / revenue) * 100;

    return {
      product_name: product.name,
      stock_item_id: `stock-${product.name.toLowerCase().replace(/\s+/g, '-')}`,
      quantity_sold: quantitySold,
      revenue,
      profit,
      margin_percentage: marginPercentage,
      avg_selling_price: avgSellingPrice,
      category: product.category,
    };
  }).sort((a, b) => b.quantity_sold - a.quantity_sold);

  // Generate least sold products (bottom 10)
  const leastSoldProducts = [...topProducts]
    .sort((a, b) => a.quantity_sold - b.quantity_sold)
    .slice(0, 10)
    .filter(p => p.quantity_sold > 0);

  // Generate hourly sales (24 hours)
  const hourlySales = [];
  for (let hour = 0; hour < 24; hour++) {
    // Peak hours: 12-14 (lunch) and 19-21 (dinner)
    let baseRevenue = 5000;
    if (hour >= 12 && hour <= 14) {
      baseRevenue = 15000 + Math.random() * 5000; // Lunch peak
    } else if (hour >= 19 && hour <= 21) {
      baseRevenue = 18000 + Math.random() * 7000; // Dinner peak
    } else if (hour >= 8 && hour <= 10) {
      baseRevenue = 8000 + Math.random() * 3000; // Breakfast
    } else if (hour >= 22 || hour <= 6) {
      baseRevenue = 2000 + Math.random() * 2000; // Late night/early morning
    } else {
      baseRevenue = 6000 + Math.random() * 4000; // Regular hours
    }

    hourlySales.push({
      hour,
      revenue: Math.floor(baseRevenue),
      orders: Math.floor(baseRevenue / 200) + Math.floor(Math.random() * 20),
    });
  }

  // Generate product category breakdown
  const categoryBreakdown = {};
  topProducts.forEach((product) => {
    if (!categoryBreakdown[product.category]) {
      categoryBreakdown[product.category] = {
        category: product.category,
        revenue: 0,
        quantity_sold: 0,
        profit: 0,
      };
    }
    categoryBreakdown[product.category].revenue += product.revenue;
    categoryBreakdown[product.category].quantity_sold += product.quantity_sold;
    categoryBreakdown[product.category].profit += product.profit;
  });

  // Generate site breakdown
  const siteBreakdown = sites.map((site) => {
    const revenue = Math.floor(Math.random() * 500000) + 100000;
    const orders = Math.floor(Math.random() * 500) + 100;
    const profitMargin = 20 + Math.random() * 15;
    const profit = Math.floor(revenue * (profitMargin / 100));

    return {
      site_id: site.id,
      site_name: site.name,
      revenue,
      orders,
      profit,
      margin_percentage: profitMargin,
    };
  });

  // Generate customers by order count distribution
  const customersByOrderCount = [];
  for (let orderCount = 1; orderCount <= 20; orderCount++) {
    const customerCount = Math.floor(Math.random() * 15) + (orderCount > 10 ? 0 : 2);
    if (customerCount > 0) {
      customersByOrderCount.push({
        order_count: orderCount,
        customer_count: customerCount,
        total_revenue: Math.floor(Math.random() * 50000) + orderCount * 500,
      });
    }
  }
  customersByOrderCount.sort((a, b) => b.order_count - a.order_count);

  // Generate payment method breakdown
  const paymentMethodBreakdown = paymentMethods.map((method) => {
    const percentage = Math.random() * 30 + 10;
    return {
      method,
      percentage: parseFloat(percentage.toFixed(2)),
      revenue: Math.floor(Math.random() * 500000) + 50000,
    };
  });
  // Normalize percentages to sum to 100
  const totalPercentage = paymentMethodBreakdown.reduce((sum, p) => sum + p.percentage, 0);
  paymentMethodBreakdown.forEach((p) => {
    p.percentage = parseFloat(((p.percentage / totalPercentage) * 100).toFixed(2));
  });

  // Generate discount analysis
  const discountCodes = [
    { code: 'WELCOME10', usage: 45, total_discount: 12500 },
    { code: 'SAVE20', usage: 32, total_discount: 18900 },
    { code: 'FESTIVAL25', usage: 28, total_discount: 22500 },
    { code: 'STUDENT15', usage: 67, total_discount: 15200 },
    { code: 'FIRST5', usage: 89, total_discount: 8900 },
  ];

  // Generate orders trend data
  const ordersTrend = dailyRevenue.map(item => ({
    date: item.date,
    orders: item.orders,
  }));

  // Generate cumulative sales growth
  let cumulativeRevenue = 0;
  const cumulativeSalesGrowth = dailyRevenue.map(item => {
    cumulativeRevenue += item.revenue;
    return {
      date: item.date,
      cumulative_revenue: cumulativeRevenue,
    };
  });

  // Generate benchmark data (previous period for comparison)
  const benchmarkData = dailyRevenue.map(item => ({
    date: item.date,
    revenue: Math.floor(item.revenue * (0.85 + Math.random() * 0.15)), // 85-100% of current
    orders: Math.floor(item.orders * (0.85 + Math.random() * 0.15)),
    profit: Math.floor(item.revenue * 0.25 * (0.85 + Math.random() * 0.15)),
  }));

  // Generate cohort analysis data (customer retention by month)
  const cohortData = [];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
  months.forEach((month, index) => {
    cohortData.push({
      cohort_month: month,
      new_customers: Math.floor(Math.random() * 50) + 20,
      month_1_retention: Math.floor(Math.random() * 30) + 40,
      month_2_retention: Math.floor(Math.random() * 25) + 30,
      month_3_retention: Math.floor(Math.random() * 20) + 20,
      repeat_purchase_rate: 30 + Math.random() * 20,
    });
  });

  // Generate funnel data (sales conversion)
  const funnelData = [
    { stage: 'Visitors', count: 10000, percentage: 100 },
    { stage: 'Add to Cart', count: 3500, percentage: 35 },
    { stage: 'Checkout Started', count: 2100, percentage: 21 },
    { stage: 'Payment Initiated', count: 1800, percentage: 18 },
    { stage: 'Purchase Completed', count: 1500, percentage: 15 },
  ];

  // Generate heatmap data (sales by product vs region/site)
  const heatmapData = [];
  const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const timeSlots = ['8-10', '10-12', '12-14', '14-16', '16-18', '18-20', '20-22'];
  weekDays.forEach(day => {
    timeSlots.forEach(slot => {
      heatmapData.push({
        day,
        time_slot: slot,
        revenue: Math.floor(Math.random() * 20000) + 5000,
        orders: Math.floor(Math.random() * 50) + 10,
      });
    });
  });

  // Calculate total revenue for waterfall
  const totalRevenue = dailyRevenue.reduce((sum, item) => sum + item.revenue, 0);

  // Generate waterfall data (financial breakdown)
  const waterfallData = [
    { label: 'Revenue', value: totalRevenue || 500000, type: 'positive' },
    { label: 'COGS', value: -(totalRevenue * 0.65 || 325000), type: 'negative' },
    { label: 'Gross Profit', value: totalRevenue * 0.35 || 175000, type: 'positive' },
    { label: 'Operating Expenses', value: -(totalRevenue * 0.15 || 75000), type: 'negative' },
    { label: 'Net Profit', value: totalRevenue * 0.20 || 100000, type: 'positive' },
  ];

  // Generate candlestick data (price movements - using product prices)
  const candlestickData = products.slice(0, 10).map((product, index) => {
    const basePrice = product.basePrice;
    const open = basePrice + (Math.random() * 20 - 10);
    const close = basePrice + (Math.random() * 20 - 10);
    const high = Math.max(open, close) + Math.random() * 15;
    const low = Math.min(open, close) - Math.random() * 15;
    return {
      product: product.name,
      date: formatDateForAPI(new Date(startDate.getTime() + index * 24 * 60 * 60 * 1000)),
      open,
      high,
      low,
      close,
    };
  });

  // Generate stock level data (inventory vs demand)
  const stockLevelData = topProducts.slice(0, 10).map(product => ({
    product_name: product.product_name,
    current_stock: Math.floor(Math.random() * 500) + 100,
    demand: product.quantity_sold,
    reorder_level: Math.floor(product.quantity_sold * 1.5),
    lead_time_days: Math.floor(Math.random() * 5) + 3,
  }));

  // Generate ABC analysis data (product classification)
  const totalProductRevenue = topProducts.reduce((sum, p) => sum + p.revenue, 0);
  let cumulativeRevenueABC = 0;
  const abcAnalysis = topProducts.map((product, index) => {
    cumulativeRevenueABC += product.revenue;
    const cumulativePercentage = (cumulativeRevenueABC / totalProductRevenue) * 100;
    let classification = 'C';
    if (cumulativePercentage <= 80) {
      classification = 'A';
    } else if (cumulativePercentage <= 95) {
      classification = 'B';
    }
    return {
      product_name: product.product_name,
      revenue: product.revenue,
      cumulative_percentage: cumulativePercentage,
      classification,
    };
  });

  return {
    customers: customerData,
    dailyRevenue,
    dailyProfit,
    dailyProfitMargin,
    revenueProfitComparison,
    topProducts: topProducts.slice(0, 10),
    leastSoldProducts,
    hourlySales,
    productCategoryBreakdown: Object.values(categoryBreakdown),
    siteBreakdown,
    customersByOrderCount,
    paymentMethodBreakdown,
    discountAnalysis: discountCodes,
    ordersTrend,
    cumulativeSalesGrowth,
    benchmarkData,
    cohortData,
    funnelData,
    heatmapData,
    waterfallData,
    candlestickData,
    stockLevelData,
    abcAnalysis,
    poInvoiceEstimationData,
  };
};

// Custom Chart Components
const WaterfallChart = ({ data }) => {
  if (!data || data.length === 0) return null;

  let runningTotal = 0;
  const processedData = data.map((item, index) => {
    const start = runningTotal;
    runningTotal += item.value;
    const end = runningTotal;
    return {
      ...item,
      start,
      end,
      height: Math.abs(item.value),
    };
  });

  const maxValue = Math.max(...processedData.map(d => Math.max(d.start, d.end)));
  const minValue = Math.min(...processedData.map(d => Math.min(d.start, d.end)));
  const range = maxValue - minValue;

  return (
    <div className="w-full h-full p-4">
      <div className="relative h-64">
        {processedData.map((item, index) => {
          const startPercent = ((item.start - minValue) / range) * 100;
          const endPercent = ((item.end - minValue) / range) * 100;
          const heightPercent = (Math.abs(item.value) / range) * 100;
          const isPositive = item.value > 0;
          const color = isPositive ? '#22c55e' : '#ef4444';

          return (
            <div key={index} className="absolute bottom-0 flex flex-col items-center" style={{ left: `${(index / processedData.length) * 100}%`, width: `${100 / processedData.length}%` }}>
              <div className="relative w-full flex-1 flex items-end justify-center">
                <div
                  className="w-3/4 rounded-t"
                  style={{
                    height: `${heightPercent}%`,
                    backgroundColor: color,
                    minHeight: '4px',
                  }}
                />
              </div>
              <div className="mt-2 text-xs text-center px-1">
                <div className="font-semibold">{item.label}</div>
                <div className="text-gray-600">₹{Math.abs(item.value).toLocaleString('en-IN')}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const FunnelChart = ({ data }) => {
  if (!data || data.length === 0) return null;

  const maxCount = Math.max(...data.map(d => d.count));

  return (
    <div className="w-full h-full p-4 flex flex-col items-center justify-center space-y-2">
      {data.map((item, index) => {
        const widthPercent = (item.count / maxCount) * 100;
        return (
          <div key={index} className="w-full flex items-center space-x-4">
            <div className="w-32 text-sm font-medium text-right">{item.stage}</div>
            <div className="flex-1 relative">
              <div
                className="h-12 rounded-lg flex items-center justify-center text-white font-semibold"
                style={{
                  width: `${widthPercent}%`,
                  backgroundColor: `hsl(${220 - index * 20}, 70%, 50%)`,
                  minWidth: '60px',
                }}
              >
                {item.count.toLocaleString('en-IN')} ({item.percentage}%)
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const HeatmapChart = ({ data }) => {
  if (!data || data.length === 0) return null;

  const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const timeSlots = ['8-10', '10-12', '12-14', '14-16', '16-18', '18-20', '20-22'];
  const maxRevenue = Math.max(...data.map(d => d.revenue));

  const getCellData = (day, slot) => {
    return data.find(d => d.day === day && d.time_slot === slot) || { revenue: 0 };
  };

  return (
    <div className="w-full h-full p-4 overflow-auto">
      <div className="inline-block min-w-full">
        <div className="grid grid-cols-8 gap-1">
          <div className="font-semibold text-sm"></div>
          {timeSlots.map(slot => (
            <div key={slot} className="font-semibold text-xs text-center">{slot}</div>
          ))}
          {weekDays.map(day => (
            <React.Fragment key={day}>
              <div className="font-semibold text-sm flex items-center">{day}</div>
              {timeSlots.map(slot => {
                const cellData = getCellData(day, slot);
                const intensity = (cellData.revenue / maxRevenue) * 100;
                return (
                  <div
                    key={`${day}-${slot}`}
                    className="w-12 h-12 rounded border border-gray-300 flex items-center justify-center text-xs font-medium"
                    style={{
                      backgroundColor: `rgba(34, 197, 94, ${intensity / 100})`,
                      color: intensity > 50 ? 'white' : 'black',
                    }}
                    title={`${day} ${slot}: ₹${cellData.revenue.toLocaleString('en-IN')}`}
                  >
                    ₹{(cellData.revenue / 1000).toFixed(0)}k
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

const CandlestickChart = ({ data }) => {
  if (!data || data.length === 0) return null;

  const allPrices = data.flatMap(d => [d.high, d.low]);
  const minPrice = Math.min(...allPrices);
  const maxPrice = Math.max(...allPrices);
  const range = maxPrice - minPrice;

  return (
    <div className="w-full h-full p-4">
      <div className="relative h-64">
        {data.map((item, index) => {
          const openPercent = ((item.open - minPrice) / range) * 100;
          const closePercent = ((item.close - minPrice) / range) * 100;
          const highPercent = ((item.high - minPrice) / range) * 100;
          const lowPercent = ((item.low - minPrice) / range) * 100;
          const isUp = item.close >= item.open;
          const color = isUp ? '#22c55e' : '#ef4444';

          return (
            <div
              key={index}
              className="absolute bottom-0 flex flex-col items-center"
              style={{ left: `${(index / data.length) * 100}%`, width: `${100 / data.length}%` }}
            >
              <div className="relative w-3/4 h-full">
                {/* High-Low line */}
                <div
                  className="absolute w-0.5 left-1/2 transform -translate-x-1/2"
                  style={{
                    bottom: `${lowPercent}%`,
                    height: `${highPercent - lowPercent}%`,
                    backgroundColor: color,
                  }}
                />
                {/* Open-Close box */}
                <div
                  className="absolute left-1/2 transform -translate-x-1/2 w-full rounded"
                  style={{
                    bottom: `${Math.min(openPercent, closePercent)}%`,
                    height: `${Math.abs(closePercent - openPercent)}%`,
                    backgroundColor: color,
                    minHeight: '2px',
                  }}
                />
              </div>
              <div className="mt-1 text-xs text-center truncate w-full px-1" title={item.product}>
                {item.product.split(' ')[0]}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const SparklineChart = ({ data, color = '#22c55e' }) => {
  if (!data || data.length === 0) return null;

  const values = data.map(d => typeof d === 'number' ? d : d.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;

  const points = values.map((value, index) => {
    const x = (index / (values.length - 1)) * 100;
    const y = 100 - ((value - min) / range) * 100;
    return `${x},${y}`;
  }).join(' ');

  return (
    <div className="w-full h-16">
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-full">
        <polyline
          points={points}
          fill="none"
          stroke={color}
          strokeWidth="2"
          vectorEffect="non-scaling-stroke"
        />
        <polygon
          points={`0,100 ${points} 100,100`}
          fill={color}
          fillOpacity="0.2"
        />
      </svg>
    </div>
  );
};

const ParetoChart = ({ data }) => {
  if (!data || data.length === 0) return null;

  const sortedData = [...data].sort((a, b) => b.revenue - a.revenue);
  const totalRevenue = sortedData.reduce((sum, item) => sum + item.revenue, 0);
  let cumulativeRevenue = 0;

  const processedData = sortedData.map((item, index) => {
    cumulativeRevenue += item.revenue;
    return {
      ...item,
      cumulativePercentage: (cumulativeRevenue / totalRevenue) * 100,
    };
  });

  const maxRevenue = Math.max(...processedData.map(d => d.revenue));

  return (
    <div className="w-full h-full p-4">
      <div className="relative h-64">
        {/* Bars */}
        <div className="absolute inset-0 flex items-end space-x-1">
          {processedData.map((item, index) => {
            const heightPercent = (item.revenue / maxRevenue) * 100;
            return (
              <div
                key={index}
                className="flex-1 bg-blue-500 rounded-t"
                style={{ height: `${heightPercent}%` }}
                title={`${item.product_name || item.name}: ₹${item.revenue.toLocaleString('en-IN')}`}
              />
            );
          })}
        </div>
        {/* Cumulative line */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none">
          <polyline
            points={processedData.map((item, index) => {
              const x = ((index + 0.5) / processedData.length) * 100;
              const y = 100 - item.cumulativePercentage;
              return `${x},${y}`;
            }).join(' ')}
            fill="none"
            stroke="#ef4444"
            strokeWidth="2"
          />
        </svg>
        {/* 80% line */}
        <div
          className="absolute left-0 right-0 border-t-2 border-dashed border-yellow-500"
          style={{ bottom: '20%' }}
        />
      </div>
      <div className="mt-2 text-xs text-gray-600">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-1">
            <div className="w-4 h-4 bg-blue-500"></div>
            <span>Revenue</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-4 h-0.5 bg-red-500"></div>
            <span>Cumulative %</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-4 h-0.5 border-t-2 border-dashed border-yellow-500"></div>
            <span>80% Line</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const CohortChart = ({ data }) => {
  if (!data || data.length === 0) return null;

  const maxRetention = Math.max(...data.map(d => Math.max(d.month_1_retention, d.month_2_retention, d.month_3_retention)));

  return (
    <div className="w-full h-full p-4 overflow-auto">
      <div className="min-w-full">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="text-left p-2">Cohort</th>
              <th className="text-center p-2">New Customers</th>
              <th className="text-center p-2">Month 1</th>
              <th className="text-center p-2">Month 2</th>
              <th className="text-center p-2">Month 3</th>
              <th className="text-center p-2">Repeat Rate</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item, index) => (
              <tr key={index} className="border-b">
                <td className="p-2 font-medium">{item.cohort_month}</td>
                <td className="p-2 text-center">{item.new_customers}</td>
                <td className="p-2">
                  <div className="flex items-center space-x-2">
                    <div className="flex-1 bg-gray-200 rounded h-4">
                      <div
                        className="bg-green-500 h-4 rounded"
                        style={{ width: `${(item.month_1_retention / maxRetention) * 100}%` }}
                      />
                    </div>
                    <span className="text-xs w-12 text-right">{item.month_1_retention}%</span>
                  </div>
                </td>
                <td className="p-2">
                  <div className="flex items-center space-x-2">
                    <div className="flex-1 bg-gray-200 rounded h-4">
                      <div
                        className="bg-blue-500 h-4 rounded"
                        style={{ width: `${(item.month_2_retention / maxRetention) * 100}%` }}
                      />
                    </div>
                    <span className="text-xs w-12 text-right">{item.month_2_retention}%</span>
                  </div>
                </td>
                <td className="p-2">
                  <div className="flex items-center space-x-2">
                    <div className="flex-1 bg-gray-200 rounded h-4">
                      <div
                        className="bg-purple-500 h-4 rounded"
                        style={{ width: `${(item.month_3_retention / maxRetention) * 100}%` }}
                      />
                    </div>
                    <span className="text-xs w-12 text-right">{item.month_3_retention}%</span>
                  </div>
                </td>
                <td className="p-2 text-center font-medium">{item.repeat_purchase_rate.toFixed(1)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ABCAnalysisChart = ({ data }) => {
  if (!data || data.length === 0) return null;

  return (
    <div className="w-full h-full p-4 overflow-auto">
      <div className="space-y-2">
        {data.map((item, index) => {
          const color = item.classification === 'A' ? '#ef4444' : item.classification === 'B' ? '#f59e0b' : '#6b7280';
          return (
            <div key={index} className="flex items-center space-x-4 p-2 border rounded">
              <div className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-white" style={{ backgroundColor: color }}>
                {item.classification}
              </div>
              <div className="flex-1">
                <div className="font-medium">{item.product_name}</div>
                <div className="text-sm text-gray-600">
                  Revenue: ₹{item.revenue.toLocaleString('en-IN')} | Cumulative: {item.cumulative_percentage.toFixed(1)}%
                </div>
              </div>
              <div className="w-32">
                <div className="bg-gray-200 rounded-full h-2">
                  <div
                    className="h-2 rounded-full"
                    style={{ width: `${item.cumulative_percentage}%`, backgroundColor: color }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-4 flex items-center space-x-4 text-sm">
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 rounded-full bg-red-500"></div>
          <span>A: Top 80% revenue</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 rounded-full bg-yellow-500"></div>
          <span>B: Next 15% revenue</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 rounded-full bg-gray-500"></div>
          <span>C: Remaining 5% revenue</span>
        </div>
      </div>
    </div>
  );
};

const SalesAnalytics = () => {
  // Log screen view
  useScreenLogging('SalesAnalytics', {
    component: 'SalesAnalytics',
  });
  const [sites, setSites] = useState([]);
  const [isSitesLoading, setIsSitesLoading] = useState(true);
  const [sitesError, setSitesError] = useState(null);
  const [selectedSite, setSelectedSite] = useState('');
  const [period, setPeriod] = useState('monthly');
  const [selectedDate, setSelectedDate] = useState(formatDateForAPI(getCurrentDate()));
  const [startDate, setStartDate] = useState(null); // YYYY-MM-DD format string
  const [endDate, setEndDate] = useState(null); // YYYY-MM-DD format string
  const [isLoading, setIsLoading] = useState(false);
  const [salesData, setSalesData] = useState(null);
  const [salesError, setSalesError] = useState(null);

  // Fetch sites on component mount
  useEffect(() => {
    const fetchSites = async () => {
      setIsSitesLoading(true);
      setSitesError(null);
      try {
        const response = await getAllSites();
        const list = Array.isArray(response?.sites)
          ? response.sites
          : Array.isArray(response)
          ? response
          : [];
        const sorted = [...list].sort((a, b) =>
          (a?.site_name || a?.name || '').localeCompare(
            b?.site_name || b?.name || ''
          )
        );
        setSites(sorted);
        // Don't auto-select - let user select from dropdown
      } catch (error) {
        console.error('Error fetching sites:', error);
        setSitesError(
          error?.message || 'Unable to load sites. Please try again later.'
        );
      } finally {
        setIsSitesLoading(false);
      }
    };

    fetchSites();
  }, []);

  // Fetch sales dashboard data from API when period, date, or site changes
  useEffect(() => {
    const loadSalesData = async () => {
      // Skip if custom period but dates not set
      if (period === 'custom') {
        if (!startDate || !endDate) {
          setIsLoading(false);
          setSalesData(null);
          return;
        }
      }

      setIsLoading(true);
      setSalesError(null);
      
      try {
        let params = {
          period: period || 'daily',
        };

        // Add date parameter for non-custom periods
        if (period !== 'custom' && selectedDate) {
          params.date = selectedDate;
        }

        // Add date range for custom period
        if (period === 'custom' && startDate && endDate) {
          params.start_date = startDate; // Already in YYYY-MM-DD format
          params.end_date = endDate; // Already in YYYY-MM-DD format
        }

        // Add site filter if selected
        if (selectedSite) {
          params.site_id = selectedSite;
        }

        // Add limit for top/least lists
        params.limit = 10;

        console.log('Sales Analytics: Fetching dashboard data with params:', params);

        const response = await salesDashboardApi.getSalesDashboard(params);

        if (response.success && response.data) {
          // Map API response to component's expected data structure
          const mappedData = {
            // Summary data
            summary: response.summary || response.data.summary,
            
            // Time series data
            revenue_over_time: response.revenueOverTime || response.data.revenue_over_time || [],
            profit_margin_over_time: response.profitMarginOverTime || response.data.profit_margin_over_time || [],
            
            // Product data
            top_products: response.topProducts || response.data.top_products || [],
            least_sold_products: response.leastSoldProducts || response.data.least_sold_products || [],
            
            // Customer data
            top_customers: response.topCustomers || response.data.top_customers || [],
            customers: response.topCustomers || response.data.top_customers || [], // For backward compatibility
            customers_by_order_count: response.customersByOrderCount || response.data.customers_by_order_count || [],
            
            // Site and category breakdown
            site_breakdown: response.siteBreakdown || response.data.site_breakdown || [],
            product_category_breakdown: response.productCategoryBreakdown || response.data.product_category_breakdown || [],
            
            // Hourly sales
            hourly_sales: response.hourlySales || response.data.hourly_sales || [],
            
            // Production metrics (if available)
            production_metrics: response.productionMetrics || response.data.production_metrics,
            top_produced_items: response.topProducedItems || response.data.top_produced_items || [],
            production_over_time: response.productionOverTime || response.data.production_over_time || [],
            production_vs_sales: response.productionVsSales || response.data.production_vs_sales,
            production_cost_breakdown: response.productionCostBreakdown || response.data.production_cost_breakdown,
            
            // Period info
            period: response.period || response.data.period,
          };

          setSalesData(mappedData);
          console.log('Sales Analytics: Dashboard data loaded successfully');
        } else {
          throw new Error(response.message || 'Failed to fetch sales dashboard data');
        }
      } catch (error) {
        console.error('Sales Analytics: Error fetching dashboard data:', error);
        setSalesError(
          error.message || 'Failed to load sales dashboard data. Please try again.'
        );
        setSalesData(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadSalesData();
  }, [period, selectedDate, startDate, endDate, selectedSite]);

  // Get site name helper
  const getSiteName = (siteId) => {
    const site = sites.find(
      (s) =>
        String(s?.site_id ?? s?.siteId ?? s?.id ?? s?._id ?? '') ===
        String(siteId)
    );
    return site?.site_name || site?.name || 'All Sites';
  };

  // Chart data preparation
  // NOTE: Updated to work with both API field names (revenue_over_time) and dummy data (dailyRevenue)
  const totalRevenueData = useMemo(() => {
    const revenueData = salesData?.revenue_over_time || salesData?.dailyRevenue;
    if (!salesData || !revenueData) return null;

    const sorted = [...revenueData].sort((a, b) => a.date.localeCompare(b.date));
    
    return {
      labels: sorted.map((item) => {
        const d = new Date(item.date);
        return formatDate(d);
      }),
      datasets: [
        {
          label: 'Total Revenue (₹)',
          data: sorted.map((item) => item.revenue),
          borderColor: '#22c55e',
          backgroundColor: '#22c55e20',
          fill: true,
          tension: 0.4,
        },
      ],
    };
  }, [salesData]);

  const profitMarginData = useMemo(() => {
    const profitData = salesData?.profit_margin_over_time || salesData?.dailyProfitMargin;
    if (!salesData || !profitData) return null;

    const sorted = [...profitData].sort((a, b) => a.date.localeCompare(b.date));
    
    return {
      labels: sorted.map((item) => {
        const d = new Date(item.date);
        return formatDate(d);
      }),
      datasets: [
        {
          label: 'Profit Margin (%)',
          data: sorted.map((item) => item.margin_percentage || item.margin),
          borderColor: '#3b82f6',
          backgroundColor: '#3b82f620',
          fill: true,
          tension: 0.4,
        },
      ],
    };
  }, [salesData]);

  // NOTE: Commented out - full customers array not provided by API (only top_customers)
  // const customerHighestSpendingData = useMemo(() => {
  //   if (!salesData || !salesData.customers) return null;

  //   const sorted = [...salesData.customers]
  //     .sort((a, b) => b.totalSpending - a.totalSpending)
  //     .slice(0, 10);
  //   
  //   return {
  //     labels: sorted.map((c) => c.customer),
  //     datasets: [
  //       {
  //         label: 'Total Spending (₹)',
  //         data: sorted.map((c) => c.totalSpending),
  //         backgroundColor: [
  //           '#22c55e',
  //           '#3b82f6',
  //           '#8b5cf6',
  //           '#f59e0b',
  //           '#ef4444',
  //           '#06b6d4',
  //           '#f472b6',
  //           '#84cc16',
  //           '#10b981',
  //           '#6366f1',
  //         ],
  //       },
  //     ],
  //   };
  // }, [salesData]);

  // const customerMostOrdersData = useMemo(() => {
  //   if (!salesData || !salesData.customers) return null;

  //   const sorted = [...salesData.customers]
  //     .sort((a, b) => b.orderCount - a.orderCount)
  //     .slice(0, 10);
  //   
  //   return {
  //     labels: sorted.map((c) => c.customer),
  //     datasets: [
  //       {
  //         label: 'Number of Orders',
  //         data: sorted.map((c) => c.orderCount),
  //         backgroundColor: '#22c55e',
  //       },
  //     ],
  //   };
  // }, [salesData]);

  // Performance vs Benchmark Charts
  // NOTE: Commented out - benchmarkData not provided by API
  // const revenueTrendData = useMemo(() => {
  //   if (!salesData || !salesData.dailyRevenue || !salesData.benchmarkData) return null;
  //   const sorted = [...salesData.dailyRevenue].sort((a, b) => a.date.localeCompare(b.date));
  //   const sortedBenchmark = [...salesData.benchmarkData].sort((a, b) => a.date.localeCompare(b.date));
  //   
  //   return {
  //     labels: sorted.map((item) => {
  //       const d = new Date(item.date);
  //       return formatDate(d);
  //     }),
  //     datasets: [
  //       {
  //         label: 'Current Revenue',
  //         data: sorted.map((item) => item.revenue),
  //         borderColor: '#22c55e',
  //         backgroundColor: '#22c55e20',
  //         fill: true,
  //         tension: 0.4,
  //       },
  //       {
  //         label: 'Benchmark Revenue',
  //         data: sortedBenchmark.map((item) => item.revenue),
  //         borderColor: '#94a3b8',
  //         backgroundColor: '#94a3b820',
  //         fill: true,
  //         tension: 0.4,
  //         borderDash: [5, 5],
  //       },
  //     ],
  //   };
  // }, [salesData]);

  // const ordersTrendData = useMemo(() => {
  //   if (!salesData || !salesData.ordersTrend || !salesData.benchmarkData) return null;
  //   const sorted = [...salesData.ordersTrend].sort((a, b) => a.date.localeCompare(b.date));
  //   const sortedBenchmark = [...salesData.benchmarkData].sort((a, b) => a.date.localeCompare(b.date));
  //   
  //   return {
  //     labels: sorted.map((item) => {
  //       const d = new Date(item.date);
  //       return formatDate(d);
  //     }),
  //     datasets: [
  //       {
  //         label: 'Current Orders',
  //         data: sorted.map((item) => item.orders),
  //         borderColor: '#3b82f6',
  //         backgroundColor: '#3b82f620',
  //         fill: true,
  //         tension: 0.4,
  //       },
  //       {
  //         label: 'Benchmark Orders',
  //         data: sortedBenchmark.map((item) => item.orders),
  //         borderColor: '#94a3b8',
  //         backgroundColor: '#94a3b820',
  //         fill: true,
  //         tension: 0.4,
  //         borderDash: [5, 5],
  //       },
  //     ],
  //   };
  // }, [salesData]);

  // const profitTrendData = useMemo(() => {
  //   if (!salesData || !salesData.dailyProfit || !salesData.benchmarkData) return null;
  //   const sorted = [...salesData.dailyProfit].sort((a, b) => a.date.localeCompare(b.date));
  //   const sortedBenchmark = [...salesData.benchmarkData].sort((a, b) => a.date.localeCompare(b.date));
  //   
  //   return {
  //     labels: sorted.map((item) => {
  //       const d = new Date(item.date);
  //       return formatDate(d);
  //     }),
  //     datasets: [
  //       {
  //         label: 'Current Profit',
  //         data: sorted.map((item) => item.profit),
  //         borderColor: '#8b5cf6',
  //         backgroundColor: '#8b5cf620',
  //         fill: true,
  //         tension: 0.4,
  //       },
  //       {
  //         label: 'Benchmark Profit',
  //         data: sortedBenchmark.map((item) => item.profit),
  //         borderColor: '#94a3b8',
  //         backgroundColor: '#94a3b820',
  //         fill: true,
  //         tension: 0.4,
  //         borderDash: [5, 5],
  //       },
  //     ],
  //   };
  // }, [salesData]);

  // Radar Chart Data for Sales by Category
  const radarChartData = useMemo(() => {
    const categories = salesData?.product_category_breakdown || salesData?.productCategoryBreakdown;
    
    if (!salesData || !categories || categories.length === 0) return null;
    
    // Calculate total revenue for normalization (optional - can use raw values)
    const totalRevenue = categories.reduce((sum, cat) => sum + (cat.revenue || 0), 0);
    
    return {
      labels: categories.map((cat) => cat.category),
      datasets: [
        {
          label: 'Revenue',
          data: categories.map((cat) => cat.revenue || 0),
          backgroundColor: 'rgba(34, 197, 94, 0.2)',
          borderColor: '#22c55e',
          borderWidth: 2,
          pointBackgroundColor: '#22c55e',
          pointBorderColor: '#fff',
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderColor: '#22c55e',
        },
        {
          label: 'Profit',
          data: categories.map((cat) => cat.profit || 0),
          backgroundColor: 'rgba(59, 130, 246, 0.2)',
          borderColor: '#3b82f6',
          borderWidth: 2,
          pointBackgroundColor: '#3b82f6',
          pointBorderColor: '#fff',
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderColor: '#3b82f6',
        },
      ],
    };
  }, [salesData]);

  // NOTE: Commented out - stacked area chart replaced with radar chart
  // const stackedAreaData = useMemo(() => {
  //   const categories = salesData?.product_category_breakdown || salesData?.productCategoryBreakdown;
  //   const revenueData = salesData?.revenue_over_time || salesData?.dailyRevenue;
  //   const products = salesData?.top_products || salesData?.topProducts;
  //   
  //   if (!salesData || !categories || !revenueData || !products) return null;
  //   const sorted = [...revenueData].sort((a, b) => a.date.localeCompare(b.date));
  //   const categoryList = [...new Set(products.map(p => p.category))];
  //   
  //   return {
  //     labels: sorted.map((item) => {
  //       const d = new Date(item.date);
  //       return formatDate(d);
  //     }),
  //     datasets: categoryList.map((category, index) => {
  //       const colors = ['#22c55e', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444'];
  //       return {
  //         label: category,
  //         data: sorted.map(() => Math.floor(Math.random() * 50000) + 10000),
  //         backgroundColor: colors[index % colors.length] + '80',
  //         borderColor: colors[index % colors.length],
  //         fill: true,
  //         tension: 0.4,
  //       };
  //     }),
  //   };
  // }, [salesData]);

  // NOTE: Commented out - cumulativeSalesGrowth not provided by API
  // const cumulativeSalesGrowthData = useMemo(() => {
  //   if (!salesData || !salesData.cumulativeSalesGrowth) return null;
  //   const sorted = [...salesData.cumulativeSalesGrowth].sort((a, b) => a.date.localeCompare(b.date));
  //   
  //   return {
  //     labels: sorted.map((item) => {
  //       const d = new Date(item.date);
  //       return formatDate(d);
  //     }),
  //     datasets: [
  //       {
  //         label: 'Cumulative Revenue (₹)',
  //         data: sorted.map((item) => item.cumulative_revenue),
  //         borderColor: '#10b981',
  //         backgroundColor: '#10b98140',
  //         fill: true,
  //         tension: 0.4,
  //       },
  //     ],
  //   };
  // }, [salesData]);

  // Category/Product Breakdown Charts
  // NOTE: Updated to work with both API field names (top_products) and dummy data (topProducts)
  const salesByProductData = useMemo(() => {
    const products = salesData?.top_products || salesData?.topProducts;
    if (!salesData || !products) return null;
    
    return {
      labels: products.map((p) => p.product_name),
      datasets: [
        {
          label: 'Revenue (₹)',
          data: products.map((p) => p.revenue),
          backgroundColor: '#3b82f6',
        },
      ],
    };
  }, [salesData]);

  const salesByProductDonutData = useMemo(() => {
    const products = salesData?.top_products || salesData?.topProducts;
    if (!salesData || !products) return null;
    
    const colors = [
      '#22c55e',
      '#3b82f6',
      '#8b5cf6',
      '#f59e0b',
      '#ef4444',
      '#06b6d4',
      '#f472b6',
      '#84cc16',
      '#10b981',
      '#6366f1',
    ];
    
    return {
      labels: products.map((p) => p.product_name),
      datasets: [
        {
          data: products.map((p) => p.revenue),
          backgroundColor: colors.slice(0, products.length),
        },
      ],
    };
  }, [salesData]);

  // PO, Invoice, and Estimation/Quotation Line Chart Data
  const poInvoiceEstimationData = useMemo(() => {
    const data = salesData?.poInvoiceEstimationData;
    if (!salesData || !data || data.length === 0) return null;

    const sorted = [...data].sort((a, b) => a.date.localeCompare(b.date));
    
    return {
      labels: sorted.map((item) => {
        const d = new Date(item.date);
        return formatDate(d);
      }),
      datasets: [
        {
          label: 'Purchase Orders',
          data: sorted.map((item) => item.purchase_orders),
          borderColor: '#3b82f6',
          backgroundColor: '#3b82f620',
          fill: true,
          tension: 0.4,
        },
        {
          label: 'Invoices',
          data: sorted.map((item) => item.invoices),
          borderColor: '#22c55e',
          backgroundColor: '#22c55e20',
          fill: true,
          tension: 0.4,
        },
        {
          label: 'Estimation/Quotation',
          data: sorted.map((item) => item.estimations),
          borderColor: '#f59e0b',
          backgroundColor: '#f59e0b20',
          fill: true,
          tension: 0.4,
        },
      ],
    };
  }, [salesData]);

  const salesBySiteData = useMemo(() => {
    const sites = salesData?.site_breakdown || salesData?.siteBreakdown;
    if (!salesData || !sites) return null;
    
    return {
      labels: sites.map((s) => s.site_name),
      datasets: [
        {
          label: 'Revenue (₹)',
          data: sites.map((s) => s.revenue),
          backgroundColor: '#22c55e',
        },
      ],
    };
  }, [salesData]);

  const horizontalBarData = useMemo(() => {
    const topProducts = salesData?.top_products || salesData?.topProducts;
    const leastProducts = salesData?.least_sold_products || salesData?.leastSoldProducts;
    if (!salesData || !topProducts || !leastProducts) return null;
    
    return {
      labels: [
        ...topProducts.slice(0, 5).map((p) => p.product_name),
        ...leastProducts.slice(0, 5).map((p) => p.product_name),
      ],
      datasets: [
        {
          label: 'Quantity Sold',
          data: [
            ...topProducts.slice(0, 5).map((p) => p.quantity_sold),
            ...leastProducts.slice(0, 5).map((p) => p.quantity_sold),
          ],
          backgroundColor: [
            ...topProducts.slice(0, 5).map(() => '#22c55e'),
            ...leastProducts.slice(0, 5).map(() => '#ef4444'),
          ],
        },
      ],
    };
  }, [salesData]);

  const paretoChartData = useMemo(() => {
    const products = salesData?.top_products || salesData?.topProducts;
    if (!salesData || !products) return null;
    return products;
  }, [salesData]);

  // Distribution & Composition Charts
  // NOTE: Updated to work with both API field names and dummy data
  const pieChartData = useMemo(() => {
    const categories = salesData?.product_category_breakdown || salesData?.productCategoryBreakdown;
    if (!salesData || !categories) return null;
    
    return {
      labels: categories.map((c) => c.category),
      datasets: [
        {
          data: categories.map((c) => c.revenue),
          backgroundColor: [
            '#22c55e',
            '#3b82f6',
            '#8b5cf6',
            '#f59e0b',
            '#ef4444',
          ],
        },
      ],
    };
  }, [salesData]);

  const donutChartData = useMemo(() => {
    const sites = salesData?.site_breakdown || salesData?.siteBreakdown;
    if (!salesData || !sites) return null;
    const totalRevenue = sites.reduce((sum, s) => sum + s.revenue, 0);
    
    return {
      labels: sites.map((s) => s.site_name),
      datasets: [
        {
          data: sites.map((s) => s.revenue),
          backgroundColor: [
            '#22c55e',
            '#3b82f6',
            '#8b5cf6',
            '#f59e0b',
            '#ef4444',
          ],
        },
      ],
    };
  }, [salesData]);

  const stackedColumnData = useMemo(() => {
    const sites = salesData?.site_breakdown || salesData?.siteBreakdown;
    if (!salesData || !sites) return null;
    
    return {
      labels: sites.map((s) => s.site_name),
      datasets: [
        {
          label: 'Revenue',
          data: sites.map((s) => s.revenue),
          backgroundColor: '#22c55e',
        },
        {
          label: 'Profit',
          data: sites.map((s) => s.profit),
          backgroundColor: '#3b82f6',
        },
      ],
    };
  }, [salesData]);

  // Chart options
  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
      },
      tooltip: {
        enabled: true,
        callbacks: {
          label: function (context) {
            return `${context.dataset.label}: ₹${context.parsed.y.toLocaleString('en-IN')}`;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: {
          maxRotation: 45,
          minRotation: 0,
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          callback: function (value) {
            return '₹' + value.toLocaleString('en-IN');
          },
        },
      },
    },
  };

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: 'index',
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 12,
            weight: '500',
          },
        },
      },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 14,
          weight: 'bold',
        },
        bodyFont: {
          size: 13,
        },
        callbacks: {
          label: function (context) {
            if (context.dataset.label.includes('Margin')) {
              return `${context.dataset.label}: ${context.parsed.y.toFixed(2)}%`;
            }
            return `${context.dataset.label}: ₹${context.parsed.y.toLocaleString('en-IN')}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.05)',
        },
        ticks: {
          maxRotation: 45,
          minRotation: 0,
          font: {
            size: 11,
          },
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.05)',
        },
        ticks: {
          font: {
            size: 11,
          },
          callback: function (value) {
            // Check if this is profit margin chart
            if (this.chart.data.datasets[0].label.includes('Margin')) {
              return value.toFixed(1) + '%';
            }
            return '₹' + value.toLocaleString('en-IN');
          },
        },
      },
    },
  };

  // Line chart options for count-based charts (PO, Invoice, Estimation)
  const lineChartCountOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: 'index',
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 12,
            weight: '500',
          },
        },
      },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 14,
          weight: 'bold',
        },
        bodyFont: {
          size: 13,
        },
        callbacks: {
          label: function (context) {
            return `${context.dataset.label}: ${context.parsed.y.toLocaleString('en-IN')}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.05)',
        },
        ticks: {
          maxRotation: 45,
          minRotation: 0,
          font: {
            size: 11,
          },
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.05)',
        },
        ticks: {
          font: {
            size: 11,
          },
          callback: function (value) {
            return value.toLocaleString('en-IN');
          },
        },
      },
    },
  };

  const doughnutChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'right',
      },
      tooltip: {
        enabled: true,
        callbacks: {
          label: function (context) {
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((context.parsed / total) * 100).toFixed(1);
            return `${context.label}: ₹${context.parsed.toLocaleString('en-IN')} (${percentage}%)`;
          },
        },
      },
    },
  };

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'right',
      },
      tooltip: {
        enabled: true,
        callbacks: {
          label: function (context) {
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((context.parsed / total) * 100).toFixed(1);
            return `${context.label}: ₹${context.parsed.toLocaleString('en-IN')} (${percentage}%)`;
          },
        },
      },
    },
  };

  const horizontalBarOptions = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
      },
      tooltip: {
        enabled: true,
        callbacks: {
          label: function (context) {
            return `${context.dataset.label}: ${context.parsed.x.toLocaleString('en-IN')}`;
          },
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        ticks: {
          callback: function (value) {
            return value.toLocaleString('en-IN');
          },
        },
      },
      y: {
        ticks: {
          maxRotation: 0,
        },
      },
    },
  };

  const radarChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 12,
            weight: '500',
          },
        },
      },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 14,
          weight: 'bold',
        },
        bodyFont: {
          size: 13,
        },
        callbacks: {
          label: function (context) {
            return `${context.dataset.label}: ₹${context.parsed.r.toLocaleString('en-IN')}`;
          },
        },
      },
    },
    scales: {
      r: {
        beginAtZero: true,
        ticks: {
          stepSize: undefined,
          showLabelBackdrop: true,
          backdropColor: 'rgba(255, 255, 255, 0.75)',
          font: {
            size: 11,
          },
          callback: function (value) {
            if (value >= 1000000) {
              return '₹' + (value / 1000000).toFixed(1) + 'M';
            } else if (value >= 1000) {
              return '₹' + (value / 1000).toFixed(0) + 'k';
            }
            return '₹' + value.toLocaleString('en-IN');
          },
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
        },
        pointLabels: {
          font: {
            size: 12,
            weight: '500',
          },
          color: '#374151',
        },
      },
    },
  };

  const stackedBarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
      },
      tooltip: {
        enabled: true,
        callbacks: {
          label: function (context) {
            return `${context.dataset.label}: ₹${context.parsed.y.toLocaleString('en-IN')}`;
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        ticks: {
          maxRotation: 45,
          minRotation: 0,
        },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        ticks: {
          callback: function (value) {
            return '₹' + value.toLocaleString('en-IN');
          },
        },
      },
    },
  };

  // Summary statistics
  const summaryStats = useMemo(() => {
    if (!salesData) {
      return {
        totalRevenue: 0,
        totalProfit: 0,
        averageProfitMargin: 0,
        customerHighestSpending: '',
        customerMostOrders: '',
        totalCustomers: 0,
        totalOrders: 0,
        avgOrderValue: 0,
      };
    }

    // Use API summary if available (preferred)
    if (salesData.summary) {
      const summary = salesData.summary;
      const customersList = salesData.top_customers || salesData.customers || [];
      const customerHighestSpending = customersList.length > 0
        ? customersList.sort((a, b) => (b.total_spent || b.totalSpending || 0) - (a.total_spent || a.totalSpending || 0))[0]
        : null;
      
      const customerMostOrders = customersList.length > 0
        ? customersList.sort((a, b) => (b.order_count || b.orderCount || 0) - (a.order_count || a.orderCount || 0))[0]
        : null;

      return {
        totalRevenue: summary.total_revenue || 0,
        totalProfit: summary.total_profit || 0,
        averageProfitMargin: summary.avg_margin_percentage || 0,
        customerHighestSpending: customerHighestSpending?.customer_name || customerHighestSpending?.customer || '',
        customerMostOrders: customerMostOrders?.customer_name || customerMostOrders?.customer || '',
        totalCustomers: summary.total_customers || customersList.length || 0,
        totalOrders: summary.total_orders || 0,
        avgOrderValue: summary.avg_order_value || 0,
      };
    }

    // Fallback: Calculate from time series data
    const revenueData = salesData.revenue_over_time || salesData.dailyRevenue || [];
    const profitData = salesData.profit_margin_over_time || salesData.dailyProfitMargin || [];
    
    const totalRevenue = revenueData.reduce((sum, item) => sum + (item.revenue || 0), 0);
    const totalProfit = profitData.reduce((sum, item) => sum + (item.profit || 0), 0);
    const averageProfitMargin = profitData.length > 0 
      ? profitData.reduce((sum, item) => sum + (item.margin_percentage || item.margin || 0), 0) / profitData.length 
      : 0;
    
    // Use top_customers from API if available, otherwise fallback to customers array
    const customersList = salesData.top_customers || salesData.customers || [];
    const customerHighestSpending = customersList.length > 0
      ? customersList.sort((a, b) => (b.total_spent || b.totalSpending || 0) - (a.total_spent || a.totalSpending || 0))[0]
      : null;
    
    const customerMostOrders = customersList.length > 0
      ? customersList.sort((a, b) => (b.order_count || b.orderCount || 0) - (a.order_count || a.orderCount || 0))[0]
      : null;

    return {
      totalRevenue,
      totalProfit,
      averageProfitMargin,
      customerHighestSpending: customerHighestSpending?.customer_name || customerHighestSpending?.customer || '',
      customerMostOrders: customerMostOrders?.customer_name || customerMostOrders?.customer || '',
      totalCustomers: customersList.length,
      totalOrders: revenueData.reduce((sum, item) => sum + (item.orders || 0), 0),
      avgOrderValue: revenueData.length > 0 ? totalRevenue / revenueData.reduce((sum, item) => sum + (item.orders || 0), 1) : 0,
    };
  }, [salesData]);

  return (
    <div className="w-full h-full">
      {/* Header with Filters */}
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className={`${typography.h2} text-black mb-1`}>Sales Analytics</h1>
          <p className={`${typography.p} text-gray-600 text-sm`}>
            Comprehensive sales performance overview
          </p>
        </div>
        
        {/* Compact Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={period}
            onChange={(e) => {
              setPeriod(e.target.value);
              if (e.target.value !== 'custom') {
                setStartDate(null);
                setEndDate(null);
              }
            }}
            className="h-10 px-4 bg-white rounded-lg border border-gray-300 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40 text-sm"
          >
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="custom">Custom Range</option>
          </select>

          {period !== 'custom' ? (
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="h-10 px-4 bg-white rounded-lg border border-gray-300 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40 text-sm"
            />
          ) : (
            <>
              <input
                type="date"
                value={startDate || ''}
                onChange={(e) => setStartDate(e.target.value || null)}
                placeholder="Start Date"
                className="h-10 px-4 bg-white rounded-lg border border-gray-300 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40 text-sm"
              />
              <input
                type="date"
                value={endDate || ''}
                onChange={(e) => setEndDate(e.target.value || null)}
                placeholder="End Date"
                className="h-10 px-4 bg-white rounded-lg border border-gray-300 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40 text-sm"
              />
            </>
          )}

          <select
            value={selectedSite}
            onChange={(e) => setSelectedSite(e.target.value)}
            disabled={isSitesLoading}
            className="h-10 px-4 bg-white rounded-lg border border-gray-300 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40 text-sm"
          >
            {isSitesLoading ? (
              <option>Loading sites...</option>
            ) : sitesError ? (
              <option>Error</option>
            ) : (
              <>
                <option value="">Select a site</option>
                {sites.map((site) => {
                  const siteId =
                    site?.site_id ??
                    site?.siteId ??
                    site?.id ??
                    site?._id ??
                    '';
                  const siteName =
                    site?.site_name || site?.name || `Site ${siteId}`;
                  return (
                    <option key={siteId} value={String(siteId)}>
                      {siteName}
                    </option>
                  );
                })}
              </>
            )}
          </select>
        </div>
      </div>

      {/* Error State */}
      {salesError && !isLoading && (
        <Card.Gradient className="p-6 mb-6 border border-red-200 bg-red-50">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className={typography.h5} style={{ color: '#dc2626', marginBottom: '0.25rem' }}>
                Error Loading Sales Data
              </h3>
              <p className={typography.p} style={{ color: '#991b1b' }}>
                {salesError}
              </p>
            </div>
            <button
              onClick={() => {
                setSalesError(null);
                // Trigger reload by updating a dependency
                setSelectedDate(formatDateForAPI(getCurrentDate()));
              }}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
            >
              Retry
            </button>
          </div>
        </Card.Gradient>
      )}

      {/* KPI Cards - Top Row */}
      {!isLoading && !salesError && salesData && (salesData.top_customers?.length > 0 || salesData.customers?.length > 0) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2 mb-6">
          <Card.Gradient
            className="p-5"
            style={{
              background: 'linear-gradient(to bottom right, #3b82f6, #2563eb)',
              boxShadow: '0px 15px 30px 0px rgba(0,0,0,0.10)',
              color: '#ffffff',
            }}
          >
            <p className="text-sm opacity-90 mb-1" style={{ color: '#ffffff' }}>Total Revenue</p>
            <p className="text-2xl font-bold" style={{ color: '#ffffff' }}>
              ₹{(summaryStats.totalRevenue / 1000).toFixed(1)}k
            </p>
            <p className="text-xs opacity-75 mt-1" style={{ color: '#ffffff' }}>All time</p>
          </Card.Gradient>
          <Card.Gradient
            className="p-5"
            style={{
              background: 'linear-gradient(to bottom right, #22c55e, #16a34a)',
              boxShadow: '0px 15px 30px 0px rgba(0,0,0,0.10)',
              color: '#ffffff',
            }}
          >
            <p className="text-sm opacity-90 mb-1" style={{ color: '#ffffff' }}>Total Profit</p>
            <p className="text-2xl font-bold" style={{ color: '#ffffff' }}>
              ₹{(summaryStats.totalProfit / 1000).toFixed(1)}k
            </p>
            <p className="text-xs opacity-75 mt-1" style={{ color: '#ffffff' }}>{summaryStats.averageProfitMargin.toFixed(1)}% margin</p>
          </Card.Gradient>
          <Card.Gradient
            className="p-5"
            style={{
              background: 'linear-gradient(to bottom right, #a855f7, #9333ea)',
              boxShadow: '0px 15px 30px 0px rgba(0,0,0,0.10)',
              color: '#ffffff',
            }}
          >
            <p className="text-sm opacity-90 mb-1" style={{ color: '#ffffff' }}>Avg Margin</p>
            <p className="text-2xl font-bold" style={{ color: '#ffffff' }}>
              {summaryStats.averageProfitMargin.toFixed(1)}%
            </p>
            <p className="text-xs opacity-75 mt-1" style={{ color: '#ffffff' }}>Profit ratio</p>
          </Card.Gradient>
          <Card.Gradient
            className="p-5"
            style={{
              background: 'linear-gradient(to bottom right, #f97316, #ea580c)',
              boxShadow: '0px 15px 30px 0px rgba(0,0,0,0.10)',
              color: '#ffffff',
            }}
          >
            <p className="text-sm opacity-90 mb-1" style={{ color: '#ffffff' }}>Top Customer</p>
            <p className="text-lg font-bold truncate" style={{ color: '#ffffff' }}>
              {summaryStats.customerHighestSpending.split(' ')[0]}
            </p>
            <p className="text-xs opacity-75 mt-1" style={{ color: '#ffffff' }}>Highest spender</p>
          </Card.Gradient>
          <Card.Gradient
            className="p-5"
            style={{
              background: 'linear-gradient(to bottom right, #ec4899, #db2777)',
              boxShadow: '0px 15px 30px 0px rgba(0,0,0,0.10)',
              color: '#ffffff',
            }}
          >
            <p className="text-sm opacity-90 mb-1" style={{ color: '#ffffff' }}>Most Orders</p>
            <p className="text-lg font-bold truncate" style={{ color: '#ffffff' }}>
              {summaryStats.customerMostOrders.split(' ')[0]}
            </p>
            <p className="text-xs opacity-75 mt-1" style={{ color: '#ffffff' }}>Most active</p>
          </Card.Gradient>
        </div>
      )}

      {/* Loading State - Skeleton Cards */}
      {isLoading && (
        <div className="space-y-6">
          {/* KPI Cards Skeleton */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2 mb-6">
            {[...Array(5)].map((_, index) => (
              <Card.Gradient key={index} className="p-5">
                <div className="h-4 bg-gray-200 rounded animate-pulse mb-2 w-2/3"></div>
                <div className="h-8 bg-gray-200 rounded animate-pulse mb-2 w-3/4"></div>
                <div className="h-3 bg-gray-200 rounded animate-pulse w-1/2"></div>
              </Card.Gradient>
            ))}
          </div>

          {/* Main Charts Row - 2 Column Layout Skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {[...Array(2)].map((_, index) => (
              <Card.Gradient key={index} className="p-6">
                <div className="h-6 bg-gray-200 rounded animate-pulse mb-4 w-1/3"></div>
                <div className="h-[350px] bg-gray-100 rounded animate-pulse"></div>
              </Card.Gradient>
            ))}
          </div>

          {/* Secondary Charts Row - 2 Column Layout Skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {[...Array(2)].map((_, index) => (
              <Card.Gradient key={index} className="p-6">
                <div className="h-6 bg-gray-200 rounded animate-pulse mb-4 w-1/3"></div>
                <div className="h-[350px] bg-gray-100 rounded animate-pulse"></div>
              </Card.Gradient>
            ))}
          </div>

          {/* Charts Row - 2 Column Layout Skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {[...Array(2)].map((_, index) => (
              <Card.Gradient key={index} className="p-6">
                <div className="h-6 bg-gray-200 rounded animate-pulse mb-4 w-1/3"></div>
                <div className="h-[350px] bg-gray-100 rounded animate-pulse"></div>
              </Card.Gradient>
            ))}
          </div>

          {/* Sales by Product Row - 2 Column Layout Skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {[...Array(2)].map((_, index) => (
              <Card.Gradient key={index} className="p-6">
                <div className="h-6 bg-gray-200 rounded animate-pulse mb-4 w-1/3"></div>
                <div className="h-[350px] bg-gray-100 rounded animate-pulse"></div>
              </Card.Gradient>
            ))}
          </div>

          {/* PO, Invoice & Estimation/Quotation - Full Width Skeleton */}
          <Card.Gradient className="p-6">
            <div className="h-6 bg-gray-200 rounded animate-pulse mb-4 w-1/4"></div>
            <div className="h-[350px] bg-gray-100 rounded animate-pulse"></div>
          </Card.Gradient>
        </div>
      )}

      {/* Charts Grid */}
      {/* NOTE: Modified condition to work with API data - check for any available data */}
      {!isLoading && !salesError && salesData && (salesData.revenue_over_time || salesData.dailyRevenue || salesData.top_products || salesData.top_customers) && (
        <div className="space-y-6">
          {/* Main Charts Row - 2 Column Layout - Revenue & Profit Trends */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Revenue Trend Chart */}
            {totalRevenueData && (
              <Card.Gradient className="p-6">
                <h3
                  className={typography.h5}
                  style={{ color: '#000000', marginBottom: '1rem' }}
                >
                  Revenue Trend
                </h3>
                <div style={{ height: '350px' }}>
                  <Line data={totalRevenueData} options={lineChartOptions} />
                </div>
              </Card.Gradient>
            )}

            {/* Profit Margin Trend Chart */}
            {profitMarginData && (
              <Card.Gradient className="p-6">
                <h3
                  className={typography.h5}
                  style={{ color: '#000000', marginBottom: '1rem' }}
                >
                  Profit Margin Trend
                </h3>
                <div style={{ height: '350px' }}>
                  <Line data={profitMarginData} options={lineChartOptions} />
                </div>
              </Card.Gradient>
            )}
          </div>

          {/* Secondary Charts Row - 2 Column Layout - Sales by Category (Pie) & Sales by Site */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Pie Chart: Sales by Category */}
            {pieChartData && (
              <Card.Gradient className="p-6">
                <h3
                  className={typography.h5}
                  style={{ color: '#000000', marginBottom: '1rem' }}
                >
                  Sales by Category (Pie)
                </h3>
                <div style={{ height: '350px' }}>
                  <Pie data={pieChartData} options={pieChartOptions} />
                </div>
              </Card.Gradient>
            )}

            {/* Pie Chart: Sales by Site */}
            {donutChartData && (
              <Card.Gradient className="p-6">
                <h3
                  className={typography.h5}
                  style={{ color: '#000000', marginBottom: '1rem' }}
                >
                  Sales by Site
                </h3>
                <div style={{ height: '350px' }}>
                  <Pie data={donutChartData} options={pieChartOptions} />
                </div>
              </Card.Gradient>
            )}
          </div>

          {/* Charts Row - 2 Column Layout - Top & Least Selling & Revenue by Site */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Horizontal Bar Chart */}
            {horizontalBarData && (
              <Card.Gradient className="p-6">
                <h3
                  className={typography.h5}
                  style={{ color: '#000000', marginBottom: '1rem' }}
                >
                  Top & Least Selling
                </h3>
                <div style={{ height: '350px' }}>
                  <Bar data={horizontalBarData} options={horizontalBarOptions} />
                </div>
              </Card.Gradient>
            )}

            {/* Revenue by Site */}
            {stackedColumnData && (
              <Card.Gradient className="p-6">
                <h3
                  className={typography.h5}
                  style={{ color: '#000000', marginBottom: '1rem' }}
                >
                  Revenue by Site
                </h3>
                <div style={{ height: '350px' }}>
                  <Bar data={stackedColumnData} options={stackedBarOptions} />
                </div>
              </Card.Gradient>
            )}
          </div>

          {/* Charts Row - 2 Column Layout - Sales by Product (Bar) & Sales by Product (Donut) */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Sales by Product - Bar Chart */}
            {salesByProductData && (
              <Card.Gradient className="p-6">
                <h3
                  className={typography.h5}
                  style={{ color: '#000000', marginBottom: '1rem' }}
                >
                  Sales by Product
                </h3>
                <div style={{ height: '350px' }}>
                  <Bar data={salesByProductData} options={barChartOptions} />
                </div>
              </Card.Gradient>
            )}

            {/* Sales by Product - Donut Chart */}
            {salesByProductDonutData && (
              <Card.Gradient className="p-6">
                <h3
                  className={typography.h5}
                  style={{ color: '#000000', marginBottom: '1rem' }}
                >
                  Sales by Product
                </h3>
                <div style={{ height: '350px' }}>
                  <Doughnut data={salesByProductDonutData} options={doughnutChartOptions} />
                </div>
              </Card.Gradient>
            )}
          </div>

          {/* PO, Invoice, and Estimation/Quotation Line Chart - Full Width */}
          {poInvoiceEstimationData && (
            <Card.Gradient className="p-6">
              <h3
                className={typography.h5}
                style={{ color: '#000000', marginBottom: '1rem' }}
              >
                PO, Invoice & Estimation/Quotation
              </h3>
              <div style={{ height: '350px' }}>
                <Line data={poInvoiceEstimationData} options={lineChartCountOptions} />
              </div>
            </Card.Gradient>
          )}

          {/* Sparklines Row - 3 Column Layout */}
          {/* NOTE: Commented out - Revenue Trend and Profit Trend sparkline cards */}
          {/* <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Revenue Trend</h4>
              <SparklineChart data={(salesData.revenue_over_time || salesData.dailyRevenue || []).map(d => d.revenue)} color="#22c55e" />
              <p className="text-xs text-gray-600 mt-2">
                ₹{summaryStats.totalRevenue.toLocaleString('en-IN')}
              </p>
            </div>
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Orders Trend</h4>
              <SparklineChart data={salesData.ordersTrend?.map(d => d.orders) || []} color="#3b82f6" />
              <p className="text-xs text-gray-600 mt-2">
                {salesData.ordersTrend?.reduce((sum, d) => sum + d.orders, 0) || 0} orders
              </p>
            </div>
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Profit Trend</h4>
              <SparklineChart data={(salesData.profit_margin_over_time || salesData.dailyProfit || []).map(d => d.profit || 0)} color="#8b5cf6" />
              <p className="text-xs text-gray-600 mt-2">
                ₹{summaryStats.totalProfit.toLocaleString('en-IN')}
              </p>
            </div>
          </div> */}

          {/* Additional Main Charts Row - 2 Column */}
          {/* NOTE: Commented out - cumulativeSalesGrowth not provided by API */}
          {/* <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold text-black mb-4">Cumulative Sales Growth</h3>
              <div style={{ height: '350px' }}>
                <Line data={cumulativeSalesGrowthData} options={lineChartOptions} />
              </div>
            </div>
          </div> */}

          {/* Customer Charts Row - 2 Column */}
          {/* NOTE: Commented out - cohortData and funnelData not provided by API */}
          {/* <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
            {salesData.cohortData && (
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-lg font-semibold text-black mb-4">Customer Retention</h3>
                <div style={{ height: '350px' }}>
                  <CohortChart data={salesData.cohortData} />
                </div>
              </div>
            )}

            {salesData.funnelData && (
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-lg font-semibold text-black mb-4">Sales Funnel</h3>
                <div style={{ height: '350px' }}>
                  <FunnelChart data={salesData.funnelData} />
                </div>
              </div>
            )}
          </div> */}

          {/* Financial Charts Row - 2 Column */}
          {/* NOTE: Commented out - waterfallData and candlestickData not provided by API */}
          {/* <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
            {salesData.waterfallData && (
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-lg font-semibold text-black mb-4">Financial Breakdown</h3>
                <div style={{ height: '350px' }}>
                  <WaterfallChart data={salesData.waterfallData} />
                </div>
              </div>
            )}

            {salesData.candlestickData && (
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-lg font-semibold text-black mb-4">Price Movements</h3>
                <div style={{ height: '350px' }}>
                  <CandlestickChart data={salesData.candlestickData} />
                </div>
              </div>
            )}
          </div> */}

          {/* Full Width Charts */}
          {/* Pareto Chart */}
          {/* NOTE: Commented out - Pareto Analysis chart */}
          {/* {paretoChartData && (
            <Card.Gradient className="p-6">
              <h3
                className={typography.h5}
                style={{ color: '#000000', marginBottom: '1rem' }}
              >
                Pareto Analysis - 80/20 Rule
              </h3>
              <div style={{ height: '400px' }}>
                <ParetoChart data={paretoChartData} />
              </div>
            </Card.Gradient>
          )} */}

          {/* Heatmap */}
          {/* NOTE: Commented out - heatmapData not provided by API (different from hourly_sales) */}
          {/* {salesData.heatmapData && (
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold text-black mb-4">Sales Heatmap</h3>
              <div style={{ height: '450px' }}>
                <HeatmapChart data={salesData.heatmapData} />
              </div>
            </div>
          )} */}

          {/* Inventory Charts Row - 2 Column */}
          {/* NOTE: Commented out - stockLevelData and abcAnalysis not provided by API */}
          {/* <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
            {salesData.stockLevelData && (
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-lg font-semibold text-black mb-4">Stock vs Demand</h3>
                <div style={{ height: '350px' }}>
                  <Bar
                    data={{
                      labels: salesData.stockLevelData.map(s => s.product_name),
                      datasets: [
                        {
                          label: 'Current Stock',
                          data: salesData.stockLevelData.map(s => s.current_stock),
                          backgroundColor: '#22c55e',
                        },
                        {
                          label: 'Demand',
                          data: salesData.stockLevelData.map(s => s.demand),
                          backgroundColor: '#3b82f6',
                        },
                        {
                          label: 'Reorder Level',
                          data: salesData.stockLevelData.map(s => s.reorder_level),
                          backgroundColor: '#ef4444',
                        },
                      ],
                    }}
                    options={barChartOptions}
                  />
                </div>
              </div>
            )}

            {salesData.abcAnalysis && (
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-lg font-semibold text-black mb-4">ABC Analysis</h3>
                <div style={{ height: '350px' }}>
                  <ABCAnalysisChart data={salesData.abcAnalysis} />
                </div>
              </div>
            )}
          </div> */}

          {/* Additional Analytics Row - 2 Column */}
          {/* NOTE: Revenue Trend and Profit Margin moved to top section above */}

          {/* Customer Analytics Row - 2 Column */}
          {/* NOTE: Commented out - full customers array not provided by API (only top_customers) */}
          {/* <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
            {customerHighestSpendingData && (
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-lg font-semibold text-black mb-4">Top Customers</h3>
                <div style={{ height: '350px' }}>
                  <Bar data={customerHighestSpendingData} options={barChartOptions} />
                </div>
              </div>
            )}

            {customerMostOrdersData && (
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-lg font-semibold text-black mb-4">Most Orders</h3>
                <div style={{ height: '350px' }}>
                  <Bar data={customerMostOrdersData} options={barChartOptions} />
                </div>
              </div>
            )}
          </div> */}
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !salesError && (!salesData || (!salesData.revenue_over_time && !salesData.dailyRevenue && !salesData.top_products && !salesData.top_customers)) && (
        <Card.Gradient className="p-12 text-center">
          <div className="max-w-md mx-auto">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <h3
              className={typography.h4}
              style={{ color: '#000000', marginBottom: '0.5rem' }}
            >
              No sales data available
            </h3>
            <p
              className={typography.p}
              style={{ color: '#666666' }}
            >
              Please select a different date range or site to view analytics
            </p>
          </div>
        </Card.Gradient>
      )}

    </div>
  );
};

export default SalesAnalytics;

